// Feature: net-worth-dashboard
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { TrendingDown } from "lucide-react";
import type { LiabilityCategory } from "@/hooks/useNetWorthData";

interface LiabilityBreakdownCardProps {
  categories: LiabilityCategory[];
  totalLiabilities: number;
  displayCurrency: string;
  isLoading: boolean;
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function LiabilityBreakdownCard({
  categories,
  totalLiabilities,
  displayCurrency,
  isLoading,
}: LiabilityBreakdownCardProps) {
  return (
    <Card className="shadow-card hover:shadow-card-hover transition-shadow duration-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-destructive" aria-hidden="true" />
          Liability Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-3" aria-label="Loading liability breakdown">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
                <Skeleton className="h-1.5 w-full rounded-full" />
              </div>
            ))}
            <Separator />
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>
        ) : (
          <>
            <ul className="space-y-3" aria-label="Liability categories">
              {categories.map((category) => {
                const pct = totalLiabilities > 0 ? (category.total / totalLiabilities) * 100 : 0;
                return (
                  <li key={category.label} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">
                        {category.label}
                      </span>
                      <span className="font-medium tabular-nums">
                        {formatCurrency(category.total, displayCurrency)}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-destructive/60 transition-all duration-500"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
            <Separator />
            <div className="flex justify-between items-center font-semibold">
              <span>Total Liabilities</span>
              <span className="tabular-nums text-destructive">
                {formatCurrency(totalLiabilities, displayCurrency)}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
